import { get } from "lodash";
import { array, bool, number, object } from "prop-types";
import React, { useEffect, useState } from "react";
import { BrowserView, MobileView } from "react-device-detect";
import { useSelector } from "react-redux";

import { DfpComponent } from "../../ads/dfp-component";
import RecommendedStories from "../../story-templates/story-elements/RecommendedStories";
import TrendingStories from "../../story-templates/story-elements/TrendingStories";
import { LatestEditionMagazineBlock } from "../LatestEditionMagazineBlock";
import MoreStoriesFromAuthor from "../../../arrow/components/Atoms/MoreStoriesFromAuthor";
import StoryAiHubPromoWidget from "../StoryAiHubPromoWidget";
import "./ad-component.m.css";

const getAdStoryKey = (story = {}) =>
  (story["story-content-id"] || story["public-identifier"] || story["sequence-no"] || story.id || "story")
    .toString()
    .replace(/[^a-zA-Z0-9_-]/g, "-");

const AdSlot = ({ story, networkId, adIndexNo, pathIndex, isAdfreePage = false }) => {
  const [adComponent, setAdComponent] = useState(<></>);
  const storyKey = getAdStoryKey(story);

  const desktopId = `story-page-inpage-leaderboard-${storyKey}${adIndexNo >= 0 ? "-" + adIndexNo : ""}`;
  const mobileId = `story-page-inpage-mRec-ad-${storyKey}${adIndexNo >= 0 ? "-" + adIndexNo : ""}`;

  useEffect(() => {
    setAdComponent(
      <>
        <BrowserView>
          <DfpComponent
            adStyleName="ad-slot-size-728x90-300x250"
            id={desktopId}
            path={
              story["story-template"] === "live-blog"
                ? `/${networkId}/RPSG_Opn_Desktop/RPSG_Opn_Livblg_Leaderboard_${pathIndex}`
                : story["story-template"] === "photo"
                ? `/${networkId}/RPSG_Opn_Desktop/RPSG_Opn_Pht_Leaderboard_${pathIndex > 0 ? 2 : 1}`
                : `/${networkId}/RPSG_Opn_Desktop/RPSG_Opn_AS_Leaderboard_${pathIndex}`
            }
            adSlotConfigName="leaderboard_ad"
            showText={true}
            targetStory={story}
          />
        </BrowserView>
        <MobileView>
          <DfpComponent
            adStyleName="ad-slot-size-300x250"
            id={mobileId}
            path={
              story["story-template"] === "live-blog"
                ? `/${networkId}/RPSG_Opn_Mob/RPSG_Opn_Livblg_mREC${pathIndex - 1 > 0 ? "_" + (pathIndex - 1) : ""}`
                : story["story-template"] === "photo"
                ? `/${networkId}/RPSG_Opn_Mob/RPSG_Opn_Pht_mREC${pathIndex > 0 ? "_" + pathIndex : ""}`
                : `/${networkId}/RPSG_Opn_Mob/RPSG_Opn_AS_mREC${pathIndex - 1 > 0 ? "_" + (pathIndex - 1) : ""}`
            }
            adSlotConfigName="mRec_ad"
            showText={true}
            targetStory={story}
          />
        </MobileView>
      </>
    );

    return () => {
      if (window.googletag && window.googletag.pubads) {
        const slots = window.googletag
          .pubads()
          .getSlots()
          .filter((slot) => {
            const id = slot.getSlotElementId();
            return id === desktopId || id === mobileId;
          });

        if (slots.length > 0) {
          window.googletag.destroySlots(slots);
        }
      }
    };
  }, []); // runs once

  return !isAdfreePage ? adComponent : null;
};

AdSlot.propTypes = {
  story: object,
  networkId: object,
  adIndexNo: number,
  pathIndex: number,
  isAdfreePage: bool,
};

const TaboolaInArticleAd = () => {
  const rawScript = `window._taboola = window._taboola || [];
  _taboola.push({
    mode: 'thumbnails-mid',
    container: 'taboola-mid-article-thumbnails',
    placement: 'Mid Article Thumbnails',
    target_type: 'mix'
  });`;

  return (
    <>
      <div id="taboola-mid-article-thumbnails" styleName="taboola"></div>
      <script type="text/javascript" dangerouslySetInnerHTML={{ __html: rawScript }}></script>
    </>
  );
};

const AdComponent = ({ adIndexNo, isDesktop, story, recommendedStories, disableRecommendedStories }) => {
  // Has to be memoized, otherwise it will re-render on every story card render
  const qtState = useSelector((state) => get(state, ["qt"], {}));
  const qtConfig = get(qtState, ["config"], {});
  const isAdfreePage = qtConfig.subscriber === "true";

  const widgetConfig = get(qtState, ["config", "ads-config", "widgets"], {});
  const networkId = get(qtState, ["config", "ads-config", "network_id"], {});
  const taboola = get(qtState, ["config", "publisher-attributes", "taboola"], true);
  const { magazineWidget = "" } = widgetConfig;

  const getFilteredAddComp = (taboola) => {
    const original = {
      0: (
        <>
          <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={1} isAdfreePage={isAdfreePage} />
          <StoryAiHubPromoWidget story={story} qtConfig={qtConfig} />
        </>
      ),
      1: <TaboolaInArticleAd />,
      2: (
        <>
          {magazineWidget.show ? (
            <LatestEditionMagazineBlock story={story} qtConfig={qtConfig} />
          ) : (
            <AdSlot
              story={story}
              networkId={networkId}
              adIndexNo={adIndexNo}
              pathIndex={2}
              isAdfreePage={isAdfreePage}
            />
          )}
        </>
      ),
      3: disableRecommendedStories ? null : (
        <RecommendedStories
          sectionId={story?.sections[0]?.id}
          storyId={story?.id}
          story={story}
          recommendedStories={recommendedStories}
          qtConfig={qtConfig}
        />
      ),
      4: <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={3} isAdfreePage={isAdfreePage} />,
      5: <MoreStoriesFromAuthor story={story} />,
      6: <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={4} isAdfreePage={isAdfreePage} />,
      7: <TrendingStories story={story} qtConfig={qtConfig} />,
      default: <></>,
    };

    if (taboola) {
      return original;
    }

    // Remove key "1" and reindex the rest (excluding default)
    const filtered = Object.entries(original).filter(([key]) => key !== "1" && key !== "default");

    const reindexed = filtered.reduce((acc, [, value], index) => {
      acc[index] = value;
      return acc;
    }, {});

    // Add back the default key
    reindexed.default = original.default;

    return reindexed;
  };

  const addComp = getFilteredAddComp(taboola);

  // Removing all ads and components/widgets from the body of sponsored articles
  if (story["story-template"] === "sponsored") {
    return null;
  }

  if (story["story-template"] === "photo") {
    return adIndexNo === 0 ? (
      <>
        <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={0} isAdfreePage={isAdfreePage} />
        <StoryAiHubPromoWidget story={story} qtConfig={qtConfig} />
      </>
    ) : adIndexNo === 1 ? (
      <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={1} isAdfreePage={isAdfreePage} />
    ) : (
      <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={2} isAdfreePage={isAdfreePage} />
    );
  }

  return (
    <>
      {adIndexNo <= 7 ? addComp[`${adIndexNo}`] : null}
      {adIndexNo > 7 && (
        <AdSlot story={story} networkId={networkId} adIndexNo={adIndexNo} pathIndex={5} isAdfreePage={isAdfreePage} />
      )}
    </>
  );
};

AdComponent.propTypes = {
  isDesktop: bool,
  adIndexNo: number,
  story: object,
  recommendedStories: array,
  disableRecommendedStories: bool,
};

export default AdComponent;
